import * as React from 'react';
import { useState } from 'react';
import { Box, SimpleGrid } from '@chakra-ui/react';
import {
  Accordion,
  Badge,
  badgeVariants,
  Button,
  buttonSizes,
  buttonVariants,
  Card,
  CardAccordion,
  CardButton,
  cardButtonVariants,
  Checkbox,
  Chip,
  chipVariants,
  ErrorMessage,
  FilterSelector,
  FormControl,
  Heading,
  HStack,
  InfoCard,
  Input,
  Link,
  Modal,
  PanelShell,
  Pressable,
  PressablePill,
  PillToggleGroup,
  Select,
  SeverityDisplay,
  SeverityLevel,
  SuccessMessage,
  Switch,
  TabBar,
  Tabs,
  Text,
  Tooltip,
  VStack,
  type BadgeProps,
  type ButtonProps,
  type ChipProps,
} from '@ayble-health/dashboard-components';
import { Section } from './Section';
import { Specimen, State } from './Specimen';
import { Tile } from './Tile';
import { Callout } from './Callout';
import { ParityBadge } from './ParityBadge';

/**
 * Everything `@ayble-health/dashboard-components` exports, rendered live from
 * the installed package.
 *
 * The Prototype tab answers "what does this app use?". This one answers "what is
 * available upstream, and how does it relate to what we have?" — which is the
 * comparison the reconciliation needs and which the Prototype tab can't make,
 * because it only ever shows components this app happens to consume.
 *
 * Everything here imports from the package, never from a local re-implementation.
 * A tab that re-implemented the components would compare our code to our code
 * and prove nothing.
 *
 * Variant lists come from the package's own introspection exports
 * (`buttonVariants`, `badgeVariants`, `chipVariants`, `cardButtonVariants`), so
 * this page cannot drift from the package the way a hand-copied list would.
 */

/** Pinned literal, same standard as `ChangeBadge` — see `parity.ts`. */
const PACKAGE_VERSION = '1.3.0';

/**
 * The package types its variant/size arrays as `ConditionalValue<...>[]` —
 * Chakra's responsive-value wrapper, so the prop can also take
 * `{ base: 'solid', md: 'outline' }`. At runtime the arrays hold plain variant
 * strings, but the wrapper type makes them unusable as React keys or as values
 * mapped straight back into the prop. This narrows to just the string members,
 * which keeps the lists enumerated from the package (no hand-copied variant
 * names to drift) without lying about the type.
 */
type VariantOf<T> = T extends string ? T : never;

const BUTTON_VARIANTS = buttonVariants as VariantOf<
  NonNullable<ButtonProps['variant']>
>[];
const BUTTON_SIZES = buttonSizes as VariantOf<
  NonNullable<ButtonProps['size']>
>[];
const BADGE_VARIANTS = badgeVariants as VariantOf<
  NonNullable<BadgeProps['variant']>
>[];
const CHIP_VARIANTS = chipVariants as VariantOf<
  NonNullable<ChipProps['variant']>
>[];

/**
 * The full export surface, module by module, as of the version above. Recorded
 * as data so the inventory is auditable and so "exported but unused here" is a
 * visible fact rather than something a reader has to infer from absence.
 */
// Variants and sizes come straight from the package's introspection exports, so
// a new variant upstream shows up here without anyone editing this file.
const ButtonSpecimen = () => (
  <Specimen
    name="Button"
    note={`variant × size, enumerated from buttonVariants / buttonSizes — ${buttonVariants.join(
      ', '
    )} × ${buttonSizes.join(', ')}`}
    badge={<ParityBadge name="Button" />}
  >
    <VStack gap={4} align="stretch" w="100%">
      {BUTTON_VARIANTS.map((variant) => (
        <HStack key={variant} gap={3} align="center" wrap="wrap">
          <Text minW="80px" fontSize="2xs" color="muted.400" fontFamily="mono">
            {variant}
          </Text>
          {BUTTON_SIZES.map((size) => (
            <Button key={size} variant={variant} size={size}>
              Save
            </Button>
          ))}
        </HStack>
      ))}
    </VStack>
  </Specimen>
);

const BadgeSpecimen = () => (
  <Specimen
    name="Badge"
    note="enumerated from badgeVariants"
    badge={<ParityBadge name="Badge" />}
  >
    {BADGE_VARIANTS.map((variant) => (
      <State key={variant} label={variant}>
        <Badge variant={variant}>{variant}</Badge>
      </State>
    ))}
  </Specimen>
);

const ChipSpecimen = () => (
  <Specimen
    name="Chip"
    note="enumerated from chipVariants — all variants including the careOS additions that landed in v1.3.0. careOS omits `outline` in favour of `symptom`; see Archive."
    badge={<ParityBadge name="Chip" />}
  >
    {CHIP_VARIANTS.map((variant) => (
      <State key={variant} label={variant}>
        <Chip variant={variant}>{variant}</Chip>
      </State>
    ))}
  </Specimen>
);

const TypeSpecimen = () => (
  <Specimen
    name="Text & Heading"
    note="text.tsx + heading.tsx"
    badge={<ParityBadge name="Text & Heading" />}
  >
    <State label="Text · default">
      <Text>Body copy — 14px / light 300 / muted.600.</Text>
    </State>
    <State label="Heading · md">
      <Heading size="md">Section heading</Heading>
    </State>
    <State label="Heading · sm">
      <Heading size="sm">Smaller heading</Heading>
    </State>
  </Specimen>
);

const StackSpecimen = () => (
  <Specimen
    name="VStack / HStack"
    note="stack.tsx — align=stretch, gap=0 by default"
    badge={<ParityBadge name="VStack / HStack" />}
  >
    <State label="VStack">
      <VStack borderWidth="1px" borderColor="cloud">
        <Box bg="feather" p={2}>
          A
        </Box>
        <Box bg="mint" p={2}>
          B
        </Box>
      </VStack>
    </State>
    <State label="HStack gap=3">
      <HStack
        gap={3}
        align="center"
        borderWidth="1px"
        borderColor="cloud"
        p={2}
      >
        <Box bg="feather" p={2}>
          A
        </Box>
        <Box bg="mint" p={2}>
          B
        </Box>
      </HStack>
    </State>
  </Specimen>
);

const PressableSpecimen = () => (
  <Specimen
    name="Pressable"
    note="native button reset; also accepts href / target / rel"
    badge={<ParityBadge name="Pressable" />}
  >
    <State label="button" wide>
      <Pressable
        w="100%"
        borderWidth="1px"
        borderColor="cloud"
        rounded="md"
        p={2}
        onClick={() => {}}
      >
        <Text my={0}>Row content</Text>
      </Pressable>
    </State>
    <State label='href + target="_blank"' wide>
      <Pressable
        href="https://ayblehealth.com"
        target="_blank"
        borderWidth="1px"
        borderColor="cloud"
        rounded="md"
        p={2}
      >
        <Text my={0}>Renders an anchor, rel="noreferrer" added</Text>
      </Pressable>
    </State>
  </Specimen>
);

const PressablePillSpecimen = () => {
  const [pressed, setPressed] = useState(true);

  return (
    <Specimen
      name="PressablePill"
      note="interactive filter pill — the pressable counterpart to inert Chip. Added in v1.3.0."
      badge={<ParityBadge name="PressablePill" />}
    >
      <State label="toggle">
        <PressablePill pressed={pressed} onClick={() => setPressed((v) => !v)}>
          {pressed ? 'Selected' : 'Unselected'}
        </PressablePill>
      </State>
    </Specimen>
  );
};

const LinkSpecimen = () => (
  <Specimen
    name="Link"
    note="brand-styled anchor"
    badge={<ParityBadge name="Link" />}
  >
    <State label="default">
      <Link href="#package-atoms">Jump to atoms</Link>
    </State>
  </Specimen>
);

const InputSpecimen = () => (
  <Specimen
    name="Input"
    note="single-line text field"
    badge={<ParityBadge name="Input" />}
  >
    <State label="default" wide>
      <Input placeholder="Email" />
    </State>
    <State label="disabled" wide>
      <Input placeholder="Email" disabled />
    </State>
  </Specimen>
);

const SelectSpecimen = () => {
  const [phase, setPhase] = useState('1');
  return (
    <Specimen
      name="Select"
      note="native select wrapper"
      badge={<ParityBadge name="Select" />}
    >
      <State label="default" wide>
        <Select
          selectedValue={phase}
          onValueChange={setPhase}
          placeholder="Select a phase"
        >
          <Select.Item label="Phase 1" value="1" />
          <Select.Item label="Phase 2" value="2" />
        </Select>
      </State>
    </Specimen>
  );
};

const CheckboxSpecimen = () => {
  const [checked, setChecked] = useState(true);
  return (
    <Specimen
      name="Checkbox"
      note="defaults to colorPalette=primary"
      badge={<ParityBadge name="Checkbox" />}
    >
      <State label="checked">
        <Checkbox isChecked={checked} onChange={setChecked}>
          Notify me
        </Checkbox>
      </State>
      <State label="disabled">
        <Checkbox isChecked isDisabled onChange={() => {}}>
          Notify me
        </Checkbox>
      </State>
    </Specimen>
  );
};

const SwitchSpecimen = () => {
  const [enabled, setEnabled] = useState(true);
  return (
    <Specimen name="Switch" note="toggle" badge={<ParityBadge name="Switch" />}>
      <State label="on">
        <Switch isChecked={enabled} onChange={setEnabled} />
      </State>
      <State label="off">
        <Switch isChecked={false} onChange={() => {}} />
      </State>
    </Specimen>
  );
};

const FormControlSpecimen = () => (
  <Specimen
    name="FormControl"
    note="label + helper / error"
    badge={<ParityBadge name="FormControl" />}
  >
    <State label="invalid" wide>
      <FormControl invalid>
        <FormControl.Label>Email</FormControl.Label>
        <Input placeholder="you@ayblehealth.com" />
        <FormControl.ErrorMessage>
          Enter a valid email.
        </FormControl.ErrorMessage>
      </FormControl>
    </State>
  </Specimen>
);

const CardSpecimen = () => (
  <Specimen
    name="Card"
    note="surface container"
    badge={<ParityBadge name="Card" />}
  >
    <State label="default" wide>
      <Card p={4} borderColor="cloud" borderWidth={1}>
        <Heading size="sm">Diagnoses</Heading>
        <Text>Type 2 diabetes, hypertension.</Text>
      </Card>
    </State>
  </Specimen>
);

// Reads `ctx.expanded` from zag's ItemState — the property the local copy got
// wrong (it read `ctx.open`, which is always undefined, so details never
// rendered). If the first item below shows its body, the package is correct.
const AccordionSpecimen = () => (
  <Specimen
    name="Accordion"
    note="collapsible sections — first item open"
    badge={<ParityBadge name="Accordion" />}
  >
    <State label="first open" wide>
      <Accordion allowMultiple index={[0]}>
        <Accordion.Item index={0}>
          {({ isExpanded }: { isExpanded: boolean }) => (
            <>
              <Accordion.Summary>
                <Text my={0}>Diet</Text>
                <Accordion.Icon />
              </Accordion.Summary>
              <Accordion.Details>
                {isExpanded && <Text my={0}>Diet details go here.</Text>}
              </Accordion.Details>
            </>
          )}
        </Accordion.Item>
        <Accordion.Item index={1}>
          {({ isExpanded }: { isExpanded: boolean }) => (
            <>
              <Accordion.Summary>
                <Text my={0}>Symptoms</Text>
                <Accordion.Icon />
              </Accordion.Summary>
              <Accordion.Details>
                {isExpanded && <Text my={0}>Symptom details go here.</Text>}
              </Accordion.Details>
            </>
          )}
        </Accordion.Item>
      </Accordion>
    </State>
  </Specimen>
);

const ModalSpecimen = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Specimen
      name="Modal"
      note="dialog overlay"
      badge={<ParityBadge name="Modal" />}
    >
      <State label="trigger">
        <Button onClick={() => setIsOpen(true)}>Open modal</Button>
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm">
          <Modal.Content>
            <Modal.CloseButton />
            <Modal.Header>Edit symptom</Modal.Header>
            <Modal.Body>
              <Text>Modal body content.</Text>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setIsOpen(false)}>Save</Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      </State>
    </Specimen>
  );
};

const TooltipSpecimen = () => (
  <Specimen
    name="Tooltip"
    note="hover hint"
    badge={<ParityBadge name="Tooltip" />}
  >
    <State label="hover the button">
      <Tooltip label="Click to copy" placement="top">
        <Button variant="outline">Hover me</Button>
      </Tooltip>
    </State>
  </Specimen>
);

const FILTER_OPTIONS = [{ label: 'Active' }, { label: 'Archived' }];

const PillToggleGroupDemo = () => {
  const [selected, setSelected] = useState<string[]>(['new']);

  return (
    <Tile
      label="PillToggleGroup"
      badge={<ParityBadge name="PillToggleGroup" />}
    >
      <PillToggleGroup
        options={[
          { value: 'new', label: 'New' },
          { value: 'inactive', label: 'Inactive' },
        ]}
        selected={selected}
        onChange={setSelected}
      />
    </Tile>
  );
};

const FilterSelectorDemo = () => {
  const [selected, setSelected] = useState(FILTER_OPTIONS[0]);
  return (
    <Tile label="FilterSelector" badge={<ParityBadge name="FilterSelector" />}>
      <FilterSelector
        options={FILTER_OPTIONS}
        selected={selected}
        onChange={setSelected}
      />
    </Tile>
  );
};

const InfoCardDemo = () => (
  <Tile label="InfoCard" badge={<ParityBadge name="InfoCard" />}>
    <VStack gap={3} align="stretch">
      <InfoCard
        title="Diet"
        subtitle="Updated yesterday"
        headingPill="Care plan"
        titleSize="lg"
        content={<Text>Track allergies, sensitivities, and preferences.</Text>}
        primaryButtonText="Edit"
        onPrimaryButtonClick={() => {}}
      />
      <InfoCard
        title="Diet"
        subtitle="primaryButtonDisabled — new upstream"
        content={
          <Text>
            The disabled state this repo previously had no way to express.
          </Text>
        }
        primaryButtonText="Edit"
        primaryButtonDisabled
        onPrimaryButtonClick={() => {}}
      />
    </VStack>
  </Tile>
);

const CardAccordionDemo = () => {
  const [expanded, setExpanded] = useState<string | null>('Diet');
  return (
    <Tile
      label="CardAccordion"
      note="chevron is the ChevronRight icon upstream, not a PNG asset"
      badge={<ParityBadge name="CardAccordion" />}
    >
      <CardAccordion
        items={[
          {
            title: 'Diet',
            summary: <Text my={0}>Tap to expand</Text>,
            content: <Text my={0}>Diet details go here.</Text>,
          },
          {
            title: 'Symptoms',
            canBeChecked: true,
            isChecked: true,
            summary: <Text my={0}>Tap to expand</Text>,
            content: <Text my={0}>Symptom details go here.</Text>,
          },
        ]}
        expandedItem={expanded}
        setExpandedItem={setExpanded}
      />
    </Tile>
  );
};

// Secondary is the case that was broken locally: it mapped onto a Button
// variant the Aug 13 audit deleted, so it rendered identically to Primary.
const CardButtonDemo = () => (
  <Tile
    label="CardButton"
    note={`enumerated from cardButtonVariants — ${cardButtonVariants.join(
      ', '
    )}. Secondary should read seafoam, not evergreen.`}
    badge={<ParityBadge name="CardButton" />}
  >
    <VStack gap={2}>
      {cardButtonVariants.map((variant) => (
        <CardButton key={variant} text={variant} variant={variant} />
      ))}
    </VStack>
  </Tile>
);

const PanelShellDemo = () => (
  <Tile
    label="PanelShell"
    note="h and bg are new upstream"
    badge={<ParityBadge name="PanelShell" />}
  >
    <PanelShell maxW={320}>
      <Heading size="sm">Schedule a visit</Heading>
      <Text>Panel content goes here.</Text>
    </PanelShell>
  </Tile>
);

const TabsDemo = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <Tile label="Tabs" badge={<ParityBadge name="Tabs" />}>
      <Tabs
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        tabs={[
          { label: 'Diet', content: <Text>Diet panel</Text> },
          { label: 'Symptoms', content: <Text>Symptoms panel</Text> },
        ]}
      />
    </Tile>
  );
};

// TabBar is the one export nothing here consumes. Rendered so the choice to
// adopt it (and retire ProviderHome/CountTabs.tsx) can be made by looking.
const TabBarDemo = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <Tile
      label="TabBar"
      note="Not used in this app. ProviderHome/CountTabs.tsx renders the same tab-bar-with-counts pattern locally."
      badge={<ParityBadge name="TabBar" />}
    >
      <TabBar
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        fitContent
        tabs={[
          { label: 'Today', count: 10 },
          { label: 'Tomorrow', count: 4 },
        ]}
      />
    </Tile>
  );
};

const MessagesDemo = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    <Tile label="ErrorMessage" badge={<ParityBadge name="ErrorMessage" />}>
      <ErrorMessage message="Something went wrong. Please try again." />
    </Tile>
    <Tile label="SuccessMessage" badge={<ParityBadge name="SuccessMessage" />}>
      <SuccessMessage message="Saved!" />
    </Tile>
  </SimpleGrid>
);

const SeverityDemo = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    <Tile
      label="SeverityLevel"
      note="deprecated on both sides — see Archive"
      badge={<ParityBadge name="Severity" />}
    >
      <VStack gap={2} align="flex-start">
        {[1, 2, 3].map((level) => (
          <SeverityLevel key={level} level={level} />
        ))}
      </VStack>
    </Tile>
    <Tile
      label="SeverityDisplay"
      note="deprecated on both sides — see Archive"
      badge={<ParityBadge name="Severity" />}
    >
      <SeverityDisplay label="Apple" level={2} />
    </Tile>
  </SimpleGrid>
);

const DatePickerReference = () => (
  <Tile
    label="DatePicker"
    note="DatePicker/DatePicker.tsx + CustomChevron + types"
    badge={<ParityBadge name="DatePicker" />}
  >
    <Text>
      Driven by a live host-availability map and timezone, so it needs the
      appointment scheduler's data to render meaningfully — see AppointmentsPage
      for it in context. Upstream it now carries its own{' '}
      <Text as="span" fontFamily="mono" fontSize="xs">
        formatDateToDayOnly
      </Text>{' '}
      and types instead of importing them from this app.
    </Text>
  </Tile>
);

export const PackageAudit = () => (
  <VStack gap={6} align="stretch">
    <Callout
      title={`Rendered from @ayble-health/dashboard-components@${PACKAGE_VERSION}`}
    >
      Every component below is imported from the installed package, not
      re-implemented here — so this tab shows what upstream actually ships.
      Hover any parity tag for the per-component detail.
    </Callout>

    <Section
      id="package-atoms"
      title="Package atoms"
      description="The shared primitives, live from the package. Variant lists are
        enumerated from the package's own introspection exports, so a new variant
        upstream appears here without anyone editing this file."
    >
      <VStack gap={4} align="stretch">
        <ButtonSpecimen />
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
          <TypeSpecimen />
          <BadgeSpecimen />
          <ChipSpecimen />
          <StackSpecimen />
          <InputSpecimen />
          <FormControlSpecimen />
          <CheckboxSpecimen />
          <SwitchSpecimen />
          <SelectSpecimen />
          <LinkSpecimen />
          <PressableSpecimen />
          <PressablePillSpecimen />
          <CardSpecimen />
          <AccordionSpecimen />
          <ModalSpecimen />
          <TooltipSpecimen />
        </SimpleGrid>
      </VStack>
    </Section>

    <Section
      id="package-molecules"
      title="Package molecules"
      description="Composites the package ships. TabBar and Autocomplete are
        exported but not consumed here."
    >
      <VStack gap={8} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
          <PillToggleGroupDemo />
          <FilterSelectorDemo />
          <CardButtonDemo />
          <PanelShellDemo />
          <TabsDemo />
          <TabBarDemo />
        </SimpleGrid>

        <InfoCardDemo />
        <CardAccordionDemo />
        <DatePickerReference />
        <MessagesDemo />
        <SeverityDemo />
      </VStack>
    </Section>
  </VStack>
);
